import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Puff } from 'react-loader-spinner';

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@repo/ui-components/shadcn/Alert';
import { extractISODate, pushToDataLayer } from './utils';
import { formReducer, INITIAL_STATE } from './schedulerReducer';
import PersonalDetails from './PersonalDetails';
import DealerSelect from './DealerSelect';
import AppointmentSelect from './AppointmentSelect';
import Confirmation from './Confirmation';
import {
  createRemoteConsultation,
  fetchDealerData,
  getDealerAppointments,
  scheduleAppointment,
} from './api';
import {
  AppointmentDataProps,
  CustomerProcessStatusEnum,
  DealerProps,
  LeadCaptureDataLayerEventProps,
  SchedulerActionType,
  SchedulerState,
  SlotProps,
} from './BlockBuilderProspectScheduler.types';

const AnimatedEllipsis = () => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDots(prev => (prev.length < 3 ? `${prev}.` : ''));
    }, 300);
    return () => clearInterval(intervalId);
  }, []);

  return <span className="ml-0.5">{dots}</span>;
};

const formatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

interface SchedulingFormProps {
  personalSubmitButtonLabel?: string;
  dealerSubmitButtonLabel?: string;
  appointmentSubmitButtonLabel?: string;
}

const SchedulingForm: React.FC<SchedulingFormProps> = ({
  personalSubmitButtonLabel,
  dealerSubmitButtonLabel,
  appointmentSubmitButtonLabel,
}) => {
  const [state, dispatch] = useReducer(formReducer, INITIAL_STATE);
  const schedulerDivRef = useRef<HTMLDivElement>(null);

  const {
    firstName,
    lastName,
    postalCode,
    referredByCode,
    email,
    phoneNumber,
    streetAddress,
    stateAbbr,
    city,
    country,
    dealers,
    selectedDealer,
    loading,
    error,
    appointmentData,
    appointmentDays,
    appointmentTimes,
    stage,
    confirmedAppointmentData,
    confirmationScenario,
    workType,
    selectedSlot,
    appointmentNotAvailable,
  } = state;

  // Push an event when the form first loads
  useEffect(() => {
    pushToDataLayer({ event: 'prospect_form_loaded' });
  }, []);

  // Scroll to #prospect-scheduler whenever loading or stage changes
  useEffect(() => {
    if (stage !== 'personalDetails' || loading) {
      const target = document.getElementById('prospect-scheduler');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [loading, stage]);

  const handlePersonalDetailsChange = (fieldName: string, value: string) => {
    dispatch({
      type: SchedulerActionType.SET_PERSONAL_DETAIL_FIELD,
      field: fieldName as keyof SchedulerState,
      value,
    });
  };

  const handleSingleDealer = async (dealer: DealerProps, sessionId: string) => {
    const dataLayerEvent: LeadCaptureDataLayerEventProps = {
      event: 'prospect_lead_captured',
      email,
      'phone number': phoneNumber,
      'Submitted First Name': firstName,
      'Submitted Last Name': lastName,
      'street address': streetAddress,
      city,
      state: stateAbbr,
      country,
      zipcode: postalCode,
      dealer_type_crm: dealer.isCrm ? 'yes' : 'no',
      dealer_type_email: dealer.isEmail ? 'yes' : 'no',
      dealer_type_efence: dealer.isEFence ? 'yes' : 'no',
      dealer_type_company_owned: dealer.isCod ? 'yes' : 'no',
      no_dealer_available: 'no',
    };

    pushToDataLayer(dataLayerEvent);

    dispatch({ type: SchedulerActionType.SET_SELECTED_DEALER, dealer });

    if (dealer.isCrm || dealer.isEmail) {
      // Call getDealerAppointments to trigger back-end linking
      await getDealerAppointments(dealer.id, sessionId);

      dispatch({
        type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
        confirmationScenario: 'isEmailOrCrmDealer',
      });
      dispatch({ type: SchedulerActionType.SET_STAGE, stage: 'confirmation' });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
      return;
    }

    dispatch({ type: SchedulerActionType.SET_SELECTED_DEALER, dealer });
    dispatch({
      type: SchedulerActionType.SET_LOADING,
      loading: true,
      loadingMessage: 'Fetching your appointment options... Just a moment!',
    });

    const appointments = await getDealerAppointments(dealer.id, sessionId);

    if (
      appointments.processStatus === CustomerProcessStatusEnum.EXISTING_CONTACT
    ) {
      pushToDataLayer({ event: 'prospect_existing_customer' });
      dispatch({
        type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
        confirmationScenario: 'existingContact',
      });
      dispatch({ type: SchedulerActionType.SET_STAGE, stage: 'confirmation' });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
      return;
    }

    if (
      appointments.processStatus ===
        CustomerProcessStatusEnum.NO_APPOINTMENTS_AVAILABLE ||
      appointments.processStatus === CustomerProcessStatusEnum.NO_WORK_TYPES
    ) {
      dispatch({
        type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
        confirmationScenario: 'noAppointmentsAvailable',
      });
      dispatch({ type: SchedulerActionType.SET_STAGE, stage: 'confirmation' });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
      return;
    }

    dispatch({ type: SchedulerActionType.SET_APPOINTMENTS, appointments });
    dispatch({
      type: SchedulerActionType.SET_STAGE,
      stage: 'appointmentSelect',
    });
  };

  const handleMultipleDealers = (multipleDealers: DealerProps[]) => {
    const dataLayerEvent: LeadCaptureDataLayerEventProps = {
      event: 'prospect_lead_captured',
      email,
      'phone number': phoneNumber,
      'Submitted First Name': firstName,
      'Submitted Last Name': lastName,
      'street address': streetAddress,
      city,
      state: stateAbbr,
      country,
      zipcode: postalCode,
    };

    dataLayerEvent.dealer_type_crm = multipleDealers.some(
      dealer => dealer.isCrm
    )
      ? 'yes'
      : 'no';
    dataLayerEvent.dealer_type_efence = multipleDealers.some(
      dealer => dealer.isEFence
    )
      ? 'yes'
      : 'no';
    dataLayerEvent.dealer_type_email = multipleDealers.some(
      dealer => dealer.isEmail
    )
      ? 'yes'
      : 'no';
    dataLayerEvent.dealer_type_company_owned = multipleDealers.some(
      dealer => dealer.isCod
    )
      ? 'yes'
      : 'no';

    pushToDataLayer(dataLayerEvent);
    dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    dispatch({
      type: SchedulerActionType.SET_DEALER_FETCH_SUCCESS,
      dealers: multipleDealers,
    });
    dispatch({ type: SchedulerActionType.SET_STAGE, stage: 'dealerSelect' });
  };

  const handleNoDealersAvailable = () => {
    dispatch({
      type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
      confirmationScenario: 'noDealersAvailable',
    });
    dispatch({
      type: SchedulerActionType.SET_STAGE,
      stage: 'confirmation',
    });
    dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    pushToDataLayer({
      event: 'prospect_lead_captured',
      email,
      'phone number': phoneNumber,
      'Submitted First Name': firstName,
      'Submitted Last Name': lastName,
      'street address': streetAddress,
      city,
      state: stateAbbr,
      country,
      zipcode: postalCode,
      no_dealer_available: 'yes',
      dealer_type_crm: 'no',
      dealer_type_email: 'no',
      dealer_type_efence: 'no',
      dealer_type_company_owned: 'yes',
    });
  };

  const processPersonalInfo = async (requestBody: any) => {
    try {
      dispatch({
        type: SchedulerActionType.SET_LOADING,
        loading: true,
        loadingMessage: 'Finding your local dealer... Just a moment!',
      });
      const dealerResponse = await fetchDealerData(requestBody);

      dispatch({
        type: SchedulerActionType.SET_PERSONAL_DETAIL_FIELD,
        field: 'sessionId',
        value: dealerResponse.sessionId,
      });

      if (
        dealerResponse.status === 417 ||
        dealerResponse.dealers.length === 0
      ) {
        handleNoDealersAvailable();
        return;
      }

      if (dealerResponse.dealers.length === 1) {
        await handleSingleDealer(
          dealerResponse.dealers[0],
          dealerResponse.sessionId
        );
        return;
      }

      if (dealerResponse.dealers.length > 1) {
        handleMultipleDealers(dealerResponse.dealers);
      }
    } catch (err) {
      dispatch({
        type: SchedulerActionType.SET_ERROR,
        error:
          'There was an error scheduling your remote consultation. Please try again.',
      });
    }
  };

  const handlePersonalInfoSubmit = async (recaptchaToken: string) => {
    if (!recaptchaToken) {
      dispatch({
        type: SchedulerActionType.SET_ERROR,
        error: 'Please complete the reCAPTCHA challenge.',
      });
      return;
    }

    if (!postalCode) {
      dispatch({
        type: SchedulerActionType.SET_ERROR,
        error: 'Please select a street address with a valid postcode.',
      });
      return;
    }

    pushToDataLayer({
      event: 'prospect_form_submit',
      source: 'prospect-scheduler',
      prospect: {
        firstName,
        lastName,
        address: streetAddress,
        zip: postalCode,
        email,
        phone: phoneNumber,
      },
    });

    const urlParams = new URLSearchParams(window.location.search);

    // Build request body
    const requestBody = {
      firstName,
      lastName,
      email,
      phoneNumber,
      streetAddress,
      postalCode,
      city,
      stateAbbr,
      country,
      referredByCode,
      clientId: urlParams.get('client_id') || '',
      sourceSystem: 'shopify-invisible-fence',
      pageUri: window.location.href,
      marketingPromo: urlParams.get('promo') || '',
      utmSource: urlParams.get('utm_source') || '',
      utmMedium: urlParams.get('utm_medium') || '',
      utmCampaign: urlParams.get('utm_campaign') || '',
      hubspotToken: urlParams.get('hubspot_token') || '',
      recaptchaToken,
    };

    await processPersonalInfo(requestBody);
  };

  const handleDealerSelect = (dealer: DealerProps) => {
    dispatch({ type: SchedulerActionType.SET_SELECTED_DEALER, dealer });
  };

  const handleDealerSubmit = async () => {
    dispatch({
      type: SchedulerActionType.SET_LOADING,
      loading: true,
      loadingMessage: 'Fetching your appointment options... Just a moment!',
    });

    if (!selectedDealer) {
      dispatch({
        type: SchedulerActionType.SET_ERROR,
        error: 'A dealer has not been selected.',
      });
      return;
    }

    try {
      const appointments = await getDealerAppointments(
        selectedDealer.id,
        state.sessionId
      );

      if (
        appointments.processStatus ===
        CustomerProcessStatusEnum.EXISTING_CONTACT
      ) {
        pushToDataLayer({ event: 'prospect_existing_customer' });

        dispatch({
          type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
          confirmationScenario: 'existingContact',
        });
        dispatch({
          type: SchedulerActionType.SET_STAGE,
          stage: 'confirmation',
        });
        dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
        return;
      }

      if (
        appointments.processStatus ===
          CustomerProcessStatusEnum.NO_APPOINTMENTS_AVAILABLE ||
        appointments.processStatus === CustomerProcessStatusEnum.NO_WORK_TYPES
      ) {
        dispatch({
          type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
          confirmationScenario: 'noAppointmentsAvailable',
        });
        dispatch({
          type: SchedulerActionType.SET_STAGE,
          stage: 'confirmation',
        });
        dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
        return;
      }

      if (appointments.workTypes.length === 0) {
        dispatch({
          type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
          confirmationScenario: 'noAppointmentsAvailable',
        });
        dispatch({
          type: SchedulerActionType.SET_STAGE,
          stage: 'confirmation',
        });
        dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
        return;
      }

      dispatch({ type: SchedulerActionType.SET_APPOINTMENTS, appointments });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
      dispatch({
        type: SchedulerActionType.SET_STAGE,
        stage: 'appointmentSelect',
      });
    } catch (err) {
      dispatch({
        type: SchedulerActionType.SET_ERROR,
        error: 'There was an error fetching the appointment data.',
      });
    }
  };

  const handleAppointmentTypeChange = (
    appointmentsObject: AppointmentDataProps
  ) => {
    const worktypeData = appointmentsObject.workTypes[0];
    const { appointmentSlots } = worktypeData;
    const firstDay = Object.keys(appointmentSlots)[0];

    const appointmentDaysFormatted = Object.keys(appointmentSlots).map(date => {
      const [year, month, day] = date.split('-').map(Number);
      const parsedDate = new Date(year, month - 1, day);
      return {
        rawDate: date,
        formattedDate: formatter.format(parsedDate),
      };
    });

    dispatch({
      type: SchedulerActionType.SET_APPOINTMENT_DAYS_TIMES_AND_WORKTYPE,
      appointmentDays: appointmentDaysFormatted,
      appointmentTimes: appointmentSlots[firstDay].map(slot => ({
        slotDisplay: slot.slotDisplay,
        start: slot.start,
        finish: slot.finish,
      })),
      workType: worktypeData.id,
    });
  };

  const handleDayChange = (day: string) => {
    const selectedWorkType = appointmentData.workTypes.find(
      type => type.id === workType
    );
    if (!selectedWorkType) return;

    const updatedTimes = selectedWorkType.appointmentSlots[day];
    if (updatedTimes) {
      dispatch({
        type: SchedulerActionType.SET_APPOINTMENT_TIMES,
        appointmentTimes: updatedTimes.map(slot => ({
          slotDisplay: slot.slotDisplay,
          start: slot.start,
          finish: slot.finish,
        })),
      });
    }
  };

  const handleTimeChange = (timeStamp: string) => {
    const selectedWorkType = appointmentData.workTypes.find(
      type => type.id === workType
    );
    if (!selectedWorkType) return;

    const selectedDate = extractISODate(timeStamp);
    const foundSlot: SlotProps = selectedWorkType.appointmentSlots[
      selectedDate
    ].find((slot: SlotProps) => slot.start === timeStamp);

    dispatch({
      type: SchedulerActionType.SET_SELECTED_SLOT,
      selectedSlot: foundSlot,
    });
  };

  const handleNoAppointmentsAvailable = async dataLayerEvent => {
    const updatedDataLayerEvent = {
      ...dataLayerEvent,
      appointment_unavailable: 'yes',
    };
    pushToDataLayer(updatedDataLayerEvent);

    // Refresh the appointments for the user
    const updatedAppointments = await getDealerAppointments(
      selectedDealer.id,
      state.sessionId
    );
    dispatch({
      type: SchedulerActionType.SET_APPOINTMENTS,
      appointments: updatedAppointments,
    });
    handleAppointmentTypeChange(appointmentData);

    // raise error in the appointment select UI
    dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    dispatch({
      type: SchedulerActionType.SET_APPOINTMENT_NOT_AVAILABLE,
      appointmentNotAvailable: true,
    });
  };

  const handleVirtualAppointment = (dataLayerEvent, response) => {
    const updatedDataLayerEvent = {
      ...dataLayerEvent,
      dealer_type_crm: selectedDealer.isCrm ? 'yes' : 'no',
    };
    updatedDataLayerEvent.dealer_type_email = selectedDealer.isEmail
      ? 'yes'
      : 'no';
    updatedDataLayerEvent.dealer_type_efence = selectedDealer.isEFence
      ? 'yes'
      : 'no';
    updatedDataLayerEvent.dealer_type_company_owned = selectedDealer.isCod
      ? 'yes'
      : 'no';

    pushToDataLayer(updatedDataLayerEvent);

    dispatch({
      type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
      confirmationScenario: 'appointmentConfirmedVirtual',
      confirmedAppointmentData: response,
    });
    dispatch({
      type: SchedulerActionType.SET_STAGE,
      stage: 'confirmation',
    });
    dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    pushToDataLayer({ event: 'prospect_consult_scheduled' });
  };

  const handleInHomeAppointment = (dataLayerEvent, response) => {
    const updatedDataLayerEvent = {
      ...dataLayerEvent,
      dealer_type_crm: selectedDealer.isCrm ? 'yes' : 'no',
    };
    updatedDataLayerEvent.dealer_type_email = selectedDealer.isEmail
      ? 'yes'
      : 'no';
    updatedDataLayerEvent.dealer_type_efence = selectedDealer.isEFence
      ? 'yes'
      : 'no';
    updatedDataLayerEvent.dealer_type_company_owned = selectedDealer.isCod
      ? 'yes'
      : 'no';

    pushToDataLayer(updatedDataLayerEvent);
    dispatch({
      type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
      confirmationScenario: 'appointmentConfirmedInHome',
      confirmedAppointmentData: response,
    });
    dispatch({
      type: SchedulerActionType.SET_STAGE,
      stage: 'confirmation',
    });
    dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });

    pushToDataLayer({ event: 'prospect_consult_scheduled' });
  };

  const handleAppointmentSubmit = async () => {
    dispatch({
      type: SchedulerActionType.SET_APPOINTMENT_NOT_AVAILABLE,
      appointmentNotAvailable: false,
    });

    try {
      dispatch({
        type: SchedulerActionType.SET_LOADING,
        loading: true,
        loadingMessage: 'Scheduling your appointment... Almost there!',
      });

      // Send "prospect_request_consult" event first
      const dataLayerEvent = {
        event: 'prospect_request_consult',
      } as LeadCaptureDataLayerEventProps;

      const response = await scheduleAppointment(selectedSlot, state.sessionId);

      // If the appointment is unavailable (417)
      if (response.status === 417) {
        await handleNoAppointmentsAvailable(dataLayerEvent);
      } else if (
        response.workTypeName === 'Consult - Virtual Consult' ||
        response.workTypeName === 'Virtual Consult' ||
        response.workTypeName === 'Virtual Consultation'
      ) {
        handleVirtualAppointment(dataLayerEvent, response);
      } else if (
        response.workTypeName === 'Consult' ||
        response.workTypeName === 'At-Home Consult' ||
        response.workTypeName === 'EFence'
      ) {
        handleInHomeAppointment(dataLayerEvent, response);
      } else {
        // fallback scenario, 500 error or unexpected response
        dispatch({
          type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
          confirmationScenario: 'noAppointmentsAvailable',
        });
        dispatch({
          type: SchedulerActionType.SET_STAGE,
          stage: 'confirmation',
        });
        dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
      }
    } catch (err) {
      // fallback scenario, 500 error or unexpected response
      dispatch({
        type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
        confirmationScenario: 'noAppointmentsAvailable',
      });
      dispatch({
        type: SchedulerActionType.SET_STAGE,
        stage: 'confirmation',
      });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    }
  };

  const handleRemoteConsultation = async () => {
    try {
      dispatch({
        type: SchedulerActionType.SET_LOADING,
        loading: true,
        loadingMessage: 'Creating your remote consultation... Just a moment!',
      });

      await createRemoteConsultation(state.sessionId);

      dispatch({
        type: SchedulerActionType.SET_CONFIRMATION_SCENARIO,
        confirmationScenario: 'remoteConsultationConfirmed',
      });
      dispatch({
        type: SchedulerActionType.SET_STAGE,
        stage: 'confirmation',
      });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    } catch (err) {
      console.error('Error creating remote consultation:', err);
      dispatch({
        type: SchedulerActionType.SET_ERROR,
        error:
          'There was an error scheduling your remote consultation. Please try again.',
      });
      dispatch({ type: SchedulerActionType.SET_LOADING, loading: false });
    }
  };

  const resetAppointmentAvailability = () => {
    dispatch({
      type: SchedulerActionType.SET_APPOINTMENT_NOT_AVAILABLE,
      appointmentNotAvailable: false,
    });
  };

  return (
    <div
      id="prospect-scheduler"
      className="px-1 max-w-screen-xs w-full min-h-[50vh]"
      ref={schedulerDivRef}
    >
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {loading && (
        <div className="flex items-center gap-2 mt-2">
          <Puff color="#888" height={24} width={24} visible />
          <p className="font-bold text-2xl text-foreground-headline m-0">
            {state.loadingMessage ?? 'Loading'} <AnimatedEllipsis />
          </p>
        </div>
      )}
      {!loading && (
        <>
          {stage === 'personalDetails' && (
            <PersonalDetails
              firstName={firstName}
              lastName={lastName}
              email={email}
              phoneNumber={phoneNumber}
              streetAddress={streetAddress}
              postalCode={postalCode}
              referredByCode={referredByCode}
              country={country}
              stateAbbr={stateAbbr}
              city={city}
              handlePersonalDetailsChange={handlePersonalDetailsChange}
              handlePersonalInfoSubmit={handlePersonalInfoSubmit}
              loading={loading}
              error={error}
              personalSubmitButtonLabel={personalSubmitButtonLabel}
            />
          )}

          {stage === 'dealerSelect' && (
            <DealerSelect
              dealers={dealers}
              selectedDealer={selectedDealer}
              handleDealerSelect={handleDealerSelect}
              handleDealerSubmit={handleDealerSubmit}
              loading={loading}
              dealerSubmitButtonLabel={dealerSubmitButtonLabel}
            />
          )}

          {stage === 'appointmentSelect' && (
            <AppointmentSelect
              selectedDealer={selectedDealer}
              handleAppointmentTypeChange={handleAppointmentTypeChange}
              appointmentData={appointmentData}
              appointmentDays={appointmentDays}
              appointmentTimes={appointmentTimes}
              handleDayChange={handleDayChange}
              handleTimeChange={handleTimeChange}
              handleAppointmentSubmit={handleAppointmentSubmit}
              loading={loading}
              appointmentSubmitButtonLabel={appointmentSubmitButtonLabel}
              appointmentNotAvailable={appointmentNotAvailable}
              resetAppointmentAvailability={resetAppointmentAvailability}
            />
          )}

          {stage === 'confirmation' && (
            <Confirmation
              confirmedAppointmentData={confirmedAppointmentData}
              dealer={selectedDealer}
              confirmationScenario={confirmationScenario}
              handleRemoteConsultation={handleRemoteConsultation}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SchedulingForm;
